import { useState } from "react";
import type { EventSubtask } from "../../../../shared/types";
import { createEventSubtask } from "../../../../shared/types";
import { useAutoResizeInput } from "../../../../hooks/useAutoResizeInput";

interface EventSubtaskEditorProps {
  readonly subtasks: readonly EventSubtask[];
  readonly onChange: (subtasks: EventSubtask[]) => void;
}

export function EventSubtaskEditor({
  subtasks,
  onChange,
}: EventSubtaskEditorProps) {
  const [input, setInput] = useState("");
  const subtaskInput = useAutoResizeInput(input, 180);

  const sorted = [...subtasks].sort((a, b) => a.priority - b.priority);

  function handleAdd() {
    const trimmed = input.trim();
    if (!trimmed) return;
    const maxPriority =
      subtasks.length > 0 ? Math.max(...subtasks.map((s) => s.priority)) : 0;
    onChange([...subtasks, createEventSubtask(trimmed, maxPriority)]);
    setInput("");
  }

  function handleToggle(id: string) {
    onChange(subtasks.map((s) => s.id === id ? { ...s, done: !s.done } : s));
  }

  function handleRemove(id: string) {
    onChange(subtasks.filter((s) => s.id !== id));
  }

  function handleMoveUp(index: number) {
    if (index === 0) return;
    const prev = sorted[index - 1];
    const curr = sorted[index];
    const updated = subtasks.map((s) => {
      if (s.id === curr.id) return { ...s, priority: prev.priority };
      if (s.id === prev.id) return { ...s, priority: curr.priority };
      return s;
    });
    onChange(updated);
  }

  function handleMoveDown(index: number) {
    if (index === sorted.length - 1) return;
    const next = sorted[index + 1];
    const curr = sorted[index];
    const updated = subtasks.map((s) => {
      if (s.id === curr.id) return { ...s, priority: next.priority };
      if (s.id === next.id) return { ...s, priority: curr.priority };
      return s;
    });
    onChange(updated);
  }

  return (
    <div className="mb-2">
      <div className="text-[10px] text-ink-faint uppercase tracking-wide mb-1">
        Sous-tâches
      </div>
      {sorted.length > 0 && (
        <div className="space-y-1 mb-2">
          {sorted.map((subtask, index) => (
            <div
              key={subtask.id}
              className="flex items-center gap-2 group"
            >
              <button
                type="button"
                onClick={() => handleToggle(subtask.id)}
                className={`shrink-0 w-4 h-4 rounded-full border transition-colors flex items-center justify-center ${
                  subtask.done
                    ? "bg-accent border-accent"
                    : "border-border bg-surface hover:border-accent/50"
                }`}
              >
                {subtask.done && (
                  <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>

              <span className={`flex-1 text-xs break-words min-w-0 ${subtask.done ? "line-through text-ink-muted" : "text-ink"}`}>
                {subtask.title}
              </span>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  aria-label="Monter"
                  className="p-0.5 text-ink-faint hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === sorted.length - 1}
                  aria-label="Descendre"
                  className="p-0.5 text-ink-faint hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(subtask.id)}
                  aria-label={`Supprimer ${subtask.title}`}
                  className="p-0.5 text-ink-faint hover:text-danger transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-1.5 items-center">
        <input
          // eslint-disable-next-line react-hooks/refs
          ref={subtaskInput.ref}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="Ajouter une sous-tâche..."
          className="px-2.5 py-1.5 text-xs bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60 placeholder:text-ink-faint/60 transition-all"
          // eslint-disable-next-line react-hooks/refs
          style={subtaskInput.style}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!input.trim()}
          className="shrink-0 w-7 h-7 flex items-center justify-center bg-accent text-white text-xs rounded-full hover:bg-accent/90 disabled:bg-surface-alt disabled:text-ink-muted disabled:cursor-not-allowed transition-colors"
        >
          +
        </button>
      </div>
    </div>
  );
}
